import {type Json} from '@packages/utilities/json';
import {constantFalse, constantTrue} from '@packages/utilities/fp/utils';
import {
  parseExpression,
  Expressions,
  Variable,
  matchExpression,
  stringifyExpression,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  EText,
  EVariable,
  EHole,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {
  namesEqual,
  qualify,
  pluginName,
  identifier,
} from '@packages/systems/wfdl-Qualified';
import {weakMemo, singleMemo} from '@packages/utilities/memo';

import {BINDED_PILL, type SlateState as SlateEditorNodes} from './slateTextUI';

const {deconstructCall} = ContextualExpressions;

export const parseJson = (string: string): undefined | Json => {
  try {
    return JSON.parse(string);
  } catch (err: any) {
    return undefined;
  }
};

// An expression of type Text can be thought of as a chain of 0 or more calls of
// the `Append` function which stitches together other Text expressions; much
// like the JS `+` operator when used on strings:
//
// ```
// x + y + z
// ```
//
// 0 calls of calls of this operator just means we have, for example,
// `x` by itself, where `x` can be a variable, literal text, etc.
//
// In order to make it easy to work with these expressions we have the
// `deconstructAppend` function which converts such an expression to an array
// of the parts like `[x, y, z]`, or `[x]`. Inversely we have `constructAppend`
// which takes such an array and converts it back to nested applications of the
// `Append` function.
const appendVarName = qualify(pluginName('Text'), identifier('Append'));
const appendVar = EVariable(Variable.qualified(appendVarName));
const constructAppend = (passedArgs: Array<Expression | any>) => {
  let args = passedArgs;
  // If there's only 1 expression, and it's a binding, add an EText expression
  // so that we still generate a callable expression, and retain type information
  // We need to retain type information for the case when there's bindings with missing sources
  if (args.length === 1 && !Expressions.getText(args[0])) {
    args = [...args, EText('')];
  }

  return args
    .slice(0, -1)
    .reduceRight(
      (acc, arg) => Expressions._call(appendVar, [arg, acc]),
      args[args.length - 1] || EHole()
    );
};

const isAppendVariableCE = ContextualExpressions.matchExpression<boolean>({
  Variable: Variable.match({
    qualified: (name) => namesEqual(name, appendVarName),
    unqualified: constantFalse,
  }),
  _: constantFalse,
});

const isAppendVariable = matchExpression<boolean>({
  Variable: Variable.match({
    qualified: (name) => namesEqual(name, appendVarName),
    unqualified: constantFalse,
  }),
  _: constantFalse,
});
// @ts-expect-error - TS7023 - 'deconstructAppend' implicitly has return type 'any' because it does not have a return type annotation and is referenced directly or indirectly in one of its return expressions.
const deconstructAppend = (expr: Expression) => {
  const {fun, args} = Expressions.deconstructCall(expr);
  if (!isAppendVariable(fun) || args.length !== 2) return [expr];
  // @ts-expect-error - TS7022 - 'rest' implicitly has type 'any' because it does not have a type annotation and is referenced directly or indirectly in its own initializer.
  const rest = deconstructAppend(args[1]);
  return [args[0]].concat(rest);
};

type Editor = {
  children: SlateEditorNodes;
};
const LINE_DELIMITER = '\n';

/**
 * This function serializes SlateJS‘ data structure to an Expression.
 */
export const serializeInput = (editor: Editor): Expression => {
  const expressionList = editor.children.reduce<Array<any>>(
    (items, line, lineIndex) => {
      // Iterates through each line in the editor to store each node (text or binding pill)
      // into our expression list. Adds a delimiter add the end of each line (which only occurs in multi-line fields)
      line.children.forEach((node) => {
        // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'SlateNode'. | TS2339 - Property 'type' does not exist on type 'SlateNode'.
        if (node.type && node.type === BINDED_PILL) {
          // @ts-expect-error - TS2339 - Property 'token' does not exist on type 'SlateNode'.
          const json = parseJson(node.token);
          const exprParseResult = json ? parseExpression(json) : null;
          if (
            exprParseResult &&
            exprParseResult.type === 'WellformedExpression'
          ) {
            items.push(exprParseResult.val);
          }
        }

        // Otherwise, this is a text node. Let's check to make sure there's actual text
        // @ts-expect-error - TS2339 - Property 'text' does not exist on type 'SlateNode'.
        else if (node.text) {
          // @ts-expect-error - TS2339 - Property 'text' does not exist on type 'SlateNode'.
          items.push(EText(node.text));
        }
      });

      // When there's additional lines, add a delimiter
      // Additional lines would only exist for multi-line fields
      if (lineIndex < editor.children.length - 1) {
        items.push(EText(LINE_DELIMITER));
      }

      return items;
    },
    []
  );

  return expressionList.length ? constructAppend(expressionList) : EText('');
};

// Allows for default Slate value to be reused without pointing to the same object
const defaultSlateValue = {children: [{text: ''}]} as const;

/**
 * This function deserializes an Expression to SlateJS‘ data structure.
 */
export const deserializeExpressionToSlate = singleMemo((multiLine: boolean) =>
  weakMemo((expr: Expression): SlateEditorNodes => {
    // The expression is Holey, if the expression was instantiated
    // but text nor binding pills were supplied to the input
    // This block of code provides the initial value for such a case
    if (Expressions.isHoleExpression(expr)) {
      // @ts-expect-error - TS2322 - Type '{ readonly children: readonly [{ readonly text: ""; }]; }' is not assignable to type '{ children: SlateNode[]; }'.
      return [defaultSlateValue];
    }

    let lineStart = 0;
    const deconstructedValue = deconstructAppend(expr);
    return deconstructedValue.reduce(
      // @ts-expect-error - TS7006 - Parameter 'values' implicitly has an 'any' type. | TS7006 - Parameter 'expression' implicitly has an 'any' type.
      (values, expression) => {
        const textValue = Expressions.getText(expression);

        if (textValue != null) {
          // When the text value is the line delimiter
          // we'll add a new element to the children list, thus inserting a new line
          if (textValue === LINE_DELIMITER && multiLine) {
            // This adds a new line into the editor children
            lineStart += 1;
            values.push({children: [{text: ''}]});
          }

          // Otherwise this was just a normal text string
          // so push it onto children of the existing line
          else {
            values[lineStart].children.push({text: textValue});
          }
        }

        // This was a binding pill expression
        // so push it onto children of the existing line
        else {
          values[lineStart].children.push({
            type: BINDED_PILL,
            children: [{text: ''}],
            token: stringifyExpression(expression),
          });
        }

        return values;
      },

      // The initial value for state always needs an empty text element if it would otherwise be empty
      // But if there's values in the deconstructed expression list, then no need for the empty text element
      [{children: deconstructedValue.length > 0 ? [] : [{text: ''}]}]
    );
  })
);

const isNotText = (expr: ContextualExpression) =>
  !ContextualExpressions.isText(stripAnn(expr));

const deconstructContextualAppend = (
  expr: ContextualExpression
): Array<ContextualExpression> => {
  const {fun, args} = deconstructCall(expr);
  if (!isAppendVariableCE(stripAnn(fun)) || args.length !== 2) return [expr];

  // @ts-expect-error - TS2345 - Argument of type 'AnnotatedExpression<ContextualExpressionAnnotation> | undefined' is not assignable to parameter of type 'ContextualExpression'.
  const rest = deconstructContextualAppend(args[1]);
  // @ts-expect-error - TS2322 - Type '(AnnotatedExpression<ContextualExpressionAnnotation> | undefined)[]' is not assignable to type 'ContextualExpression[]'.
  return [args[0]].concat(rest);
};
const isHoleExpression = ContextualExpressions.matchExpression({
  Hole: constantTrue,
  _: constantFalse,
});
export const listOfTokens = weakMemo(
  (wholeExpr: ContextualExpression): Array<ContextualExpression> =>
    isHoleExpression(stripAnn(wholeExpr))
      ? []
      : deconstructContextualAppend(wholeExpr).filter(isNotText)
);

/**
 * If this is a label representing a path to a
 * nested value (ie JSON), only display the last
 * leaf of the path
 */
export const getShortTokenLabel = (tokenLabel: string): string => {
  const split = tokenLabel.split('.');
  const lastLeaf = split[split.length - 1];

  // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
  return lastLeaf;
};
